<template>
  <template v-if="brandconfig.BRAND === 'xs2event'">
    <!--
      Built from js-lib's ContextMenu parts rather than its UserMenu: UserMenu names the account after
      the supplier whenever the login carries a supplier_id, and the portal login has no supplier
      name, so marketplace sellers would lose the distributor name in the identity block.
    -->
    <ContextMenu v-if="userStore.isUser" @update:open="isOpen = $event">
      <ContextMenuButtonTrigger v-bind="$attrs">
        <button
          type="button"
          data-testid="menu-button"
          :aria-label="$t('Open user menu')"
          aria-haspopup="menu"
          :aria-expanded="isOpen"
          :class="[
            'flex h-target-minimum min-w-target-minimum items-center  gap-1 rounded-lg lg:px-3 lg:py-2 text-menu-link hover:bg-surface-selected justify-center',
            isOpen && 'bg-surface-selected',
          ]">
          <!-- Mobile has no room for the name: just the user icon there. -->
          <UserIcon class="size-6 flex-none lg:hidden" aria-hidden="true" />
          <span class="hidden lg:block truncate text-base font-medium">{{ userStore.distributor_name }}</span>
          <ChevronDownIcon
            class="hidden lg:block size-4 flex-none transition-transform"
            :class="isOpen && 'rotate-180'"
            aria-hidden="true" />
        </button>
      </ContextMenuButtonTrigger>

      <ContextMenuContent :aria-label="$t('Open user menu')">
        <!-- Identity: the distributor on top, the signed-in user underneath. -->
        <ContextMenuLabel
          v-if="userStore.distributor_name"
          class="truncate font-semibold text-sm text-foreground"
          :class="{ '-mb-3': username }">
          {{ userStore.distributor_name }}
        </ContextMenuLabel>
        <ContextMenuLabel v-if="username" class="truncate font-semibold text-sm text-foreground/70">
          {{ username }}
        </ContextMenuLabel>
        <ContextMenuSeparator />

        <ContextMenuButton :disabled="userStore.isIncognito" @select="router.push('/user/profile')">
          <UserIcon class="mr-2 size-5" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Manage account") }}</span>
        </ContextMenuButton>

        <ContextMenuButton v-if="userStore.isDistributor" @select="router.push('/user/bookings')">
          <CalendarDaysIcon class="mr-2 size-5" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Bookings") }}</span>
        </ContextMenuButton>

        <ContextMenuButton
          v-if="MARKETPLACE && userStore.isSupplier && userStore.roles.includes('supplier')"
          @select="router.push({ name: 'listings', query: { sorting: 'created:desc' } })">
          <ArrowPathIcon class="mr-2 size-5" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Relistings & Sales") }}</span>
        </ContextMenuButton>

        <ContextMenuButton @select="router.push({ name: 'invoices', query: { sorting: 'created:desc' } })">
          <WalletIcon class="mr-2 size-5" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Open invoices") }}</span>
        </ContextMenuButton>

        <ContextMenuButton @select="router.push({ name: 'allocations', query: { sorting: 'created:desc' } })">
          <UserGroupIcon class="mr-2 size-5" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Allocations") }}</span>
        </ContextMenuButton>

        <ContextMenuButton @select="router.push({ name: 'watchlist' })">
          <MdiBellOutline class="mr-2 size-5" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Watchlist") }}</span>
        </ContextMenuButton>

        <ContextMenuButton v-if="canImpersonate" @select="isImpersonationOpen = true">
          <EyeIcon class="mr-2 size-5" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Impersonate") }}</span>
        </ContextMenuButton>

        <ContextMenuButton
          v-if="isIT && getMissingKeysCount() > 0 && getLocale() === 'en'"
          @select="exportMissingKeys()">
          <ArrowDownCircleIcon class="mr-2 size-5" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Missing translations") }} ({{ getMissingKeysCount() }})</span>
        </ContextMenuButton>

        <ContextMenuSeparator />

        <ContextMenuButton danger data-testid="user-menu-logout" @select="userStore.signOut()">
          <ArrowRightStartOnRectangleIcon class="mr-2 size-5" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Sign out") }}</span>
        </ContextMenuButton>
      </ContextMenuContent>
    </ContextMenu>

    <RouterLink v-else-if="!userStore.isWebshop" to="/sign-in">{{ $t("Sign in") }}</RouterLink>
  </template>

  <ImpersonationDialog
    v-if="isImpersonationOpen"
    :api
    :open="isImpersonationOpen"
    @close="isImpersonationOpen = false" />
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { RouterLink, useRouter } from "vue-router";
import {
  CalendarDaysIcon,
  WalletIcon,
  UserGroupIcon,
  ArrowPathIcon,
  EyeIcon,
  ChevronDownIcon,
  ArrowDownCircleIcon,
  ArrowRightStartOnRectangleIcon,
} from "@heroicons/vue/24/outline";
import {
  ContextMenu,
  ContextMenuButton,
  ContextMenuButtonTrigger,
  ContextMenuContent,
  ContextMenuLabel,
  ContextMenuSeparator,
} from "@xs2event/js-lib/components";
import { UserIcon } from "@xs2event/js-lib/icons";
// The same bell as the watch button on events.
import MdiBellOutline from "~icons/mdi/bell-outline";
import { MARKETPLACE } from "@/config/feature-toggles";
import { useUserStore } from "@/store/user.js";
import brandconfig from "@/brandconfig";
import { exportMissingKeys, getMissingKeysCount } from "@/i18n";
import { getLocale } from "@/components/page/menu/locales";
import { ImpersonationDialog } from "@xs2event/js-lib/impersonation";
import { AccessTypeRole } from "@xs2event/js-lib/api";
import { api } from "@/helpers/api";

// Attributes (e.g. PageHeader's @click that closes the mobile menu) go to the trigger.
defineOptions({ inheritAttrs: false });

const router = useRouter();
const userStore = useUserStore();

const isOpen = ref(false);
const isImpersonationOpen = ref(false);

// An incognito session must not show whose login it is.
const username = computed(() => (userStore.isIncognito ? undefined : userStore.username));

const isIT = computed(() => userStore.roles.includes(AccessTypeRole.It));
const canImpersonate = computed(
  () => userStore.roles.includes(AccessTypeRole.Admin) || userStore.roles.includes(AccessTypeRole.Impersonator)
);
</script>
